import { computeDynamicBillSummaryNodeAmount } from "./dynamicBillSummarySerialization";
import type {
  DynamicBillSummaryNode,
  DynamicBillSummaryItemNode,
  DynamicBillSummaryTab,
} from "../types/tender.types";
import {
  RUN_MATRIX_COLUMNS,
  type RunMatrixColumn,
  type RunMatrixDiscipline,
  type RunMatrixResult,
  type RunMatrixRow,
} from "./runMatrixGenerator";
import {
  ROADS_COLUMNS,
  POS_COLUMNS,
  PRELIMS_COLUMNS,
  MAIN_SERVICES_COLUMNS,
  WORK_CATEGORY_COLUMNS,
  type SectionMatrix,
  type SectionMatrixColumn,
  type SectionMatrixRow,
} from "./siteWorksMatrixGenerator";

/**
 * The Roads & Sewers counterpart of `dynamicBillSummaryPlotMatrix.ts` — lets a
 * Dynamic Bill Summary / ad-hoc extra item fold into the Run Matrix instead of
 * the Plot Matrix. Unlike Plot Matrix's stages (which vary per tender, since
 * they're derived from `generatePlotMatrixCategoryColumns`), every Run Matrix
 * section's column set is a static catalog already exported by the same
 * generator the Run Matrix tab itself renders from (`RUN_MATRIX_COLUMNS`,
 * `ROADS_COLUMNS`, etc.) — so this file can offer the same "tabs and their
 * children" lookup with no live tender data required, and it can never drift
 * from what the Run Matrix tab actually shows.
 *
 * Spoil is deliberately excluded — its matrix is a volume balance (m³), not
 * money, so there is no Amount column to fold a Bill Summary item's rate ×
 * qty into.
 *
 * Drainage Runs is not one entry — the Run Matrix tab itself splits it into a
 * Foul Sewers / Storm Sewers discipline switcher, and each discipline has its
 * own "build-ups" sub-tab (Pump Station & Rising Main for Foul, Ponds &
 * Attenuation for Storm — `DrainageRunMatrix.tsx`). Flattened here into four
 * distinct entries so a user can target exactly the tab/sub-tab they see on
 * screen, same as every other section, rather than one combined "Drainage
 * Runs" a mapped item couldn't actually be discipline-specific within.
 */
export type RunMatrixMappingSectionKey =
  | "drainage-foul"
  | "drainage-storm"
  | "foul-build-ups"
  | "storm-build-ups"
  | "prelims"
  | "roads"
  | "main-services"
  | "substation"
  | "pos"
  | "compound";

export interface RunMatrixMappingSection {
  key: RunMatrixMappingSectionKey;
  /** Same label the Run Matrix tab's own section/discipline switcher shows. */
  label: string;
  columns: readonly (RunMatrixColumn | SectionMatrixColumn)[];
}

export const RUN_MATRIX_MAPPING_SECTIONS: readonly RunMatrixMappingSection[] = [
  { key: "drainage-foul", label: "Drainage Runs — Foul Sewers", columns: RUN_MATRIX_COLUMNS },
  { key: "drainage-storm", label: "Drainage Runs — Storm Sewers", columns: RUN_MATRIX_COLUMNS },
  { key: "foul-build-ups", label: "Foul Sewers — Pump Station & Rising Main", columns: WORK_CATEGORY_COLUMNS },
  { key: "storm-build-ups", label: "Storm Sewers — Ponds & Attenuation", columns: WORK_CATEGORY_COLUMNS },
  { key: "prelims", label: "Preliminaries", columns: PRELIMS_COLUMNS },
  { key: "roads", label: "Roads", columns: ROADS_COLUMNS },
  { key: "main-services", label: "Main Services", columns: MAIN_SERVICES_COLUMNS },
  { key: "substation", label: "Substation", columns: WORK_CATEGORY_COLUMNS },
  { key: "pos", label: "POS", columns: POS_COLUMNS },
  { key: "compound", label: "Compound", columns: WORK_CATEGORY_COLUMNS },
];

/** `drainage-foul`/`drainage-storm` → the discipline whose "Not run-specific" row(s) receive the mapping. */
const DRAINAGE_DISCIPLINE_BY_SECTION: Partial<Record<RunMatrixMappingSectionKey, RunMatrixDiscipline>> = {
  "drainage-foul": "foul",
  "drainage-storm": "storm",
};

/** Every Run Matrix section name, in the same order the Run Matrix tab's own switcher lists them. */
export const getRunMatrixMappingSectionLabels = (): { key: RunMatrixMappingSectionKey; label: string }[] =>
  RUN_MATRIX_MAPPING_SECTIONS.map(({ key, label }) => ({ key, label }));

/** Every column within the given section — the "children" of that tab. */
export const findRunMatrixMappingColumnsForSection = (
  section: string | undefined,
): readonly (RunMatrixColumn | SectionMatrixColumn)[] =>
  RUN_MATRIX_MAPPING_SECTIONS.find((s) => s.key === section)?.columns ?? [];

const isSectionNode = (
  node: DynamicBillSummaryNode,
): node is Extract<DynamicBillSummaryNode, { type: "section" }> => node.type === "section";

const collectMappedItems = (nodes: DynamicBillSummaryNode[], out: DynamicBillSummaryItemNode[]) => {
  nodes.forEach((node) => {
    if (isSectionNode(node)) {
      collectMappedItems(node.children, out);
    } else if (node.runMatrixMapping) {
      out.push(node);
    }
  });
};

export interface ResolvedRunMatrixMappedItem {
  section: RunMatrixMappingSectionKey;
  columnKey: string;
  /** Already marked up — see `computeDynamicBillSummaryNodeAmount`'s own `markupFactor` param. */
  amount: number;
}

/**
 * Gathers every item (across every Dynamic Bill Summary tab, plus any ad-hoc
 * extra-item bucket sets) that carries a `runMatrixMapping`, and resolves each
 * to its marked-up amount. Mirrors `applyDynamicBillSummaryToPlotMatrixRows`'s
 * own item-collection step in dynamicBillSummaryPlotMatrix.ts — split out as
 * its own function here (rather than one combined apply-to-everything call)
 * because a Run Matrix mapped item can target one of two structurally
 * different result shapes (`RunMatrixResult` for Drainage, `SectionMatrix` for
 * every other section), so the caller resolves once and applies per-shape
 * below.
 */
export function resolveRunMatrixMappedItems(
  dynamicTabs: DynamicBillSummaryTab[],
  markupFactor: number = 1,
  ...extraItemBucketSets: Array<Record<string, DynamicBillSummaryNode[]> | undefined>
): ResolvedRunMatrixMappedItem[] {
  const mappedItems: DynamicBillSummaryItemNode[] = [];
  dynamicTabs.forEach((tab) => collectMappedItems(tab.nodes, mappedItems));
  extraItemBucketSets.forEach((extraItemBuckets) => {
    if (extraItemBuckets) {
      Object.values(extraItemBuckets).forEach((nodes) => collectMappedItems(nodes, mappedItems));
    }
  });

  return mappedItems.flatMap((item): ResolvedRunMatrixMappedItem[] => {
    const mapping = item.runMatrixMapping;
    if (!mapping) return [];
    const amount = computeDynamicBillSummaryNodeAmount(item, markupFactor);
    if (!amount) return [];
    return [{ section: mapping.section as RunMatrixMappingSectionKey, columnKey: mapping.columnKey, amount }];
  });
}

const recomputeColumnTotals = (
  rows: SectionMatrixRow[],
  columns: readonly SectionMatrixColumn[],
): Record<string, number> => {
  const totals: Record<string, number> = {};
  columns.forEach((col) => {
    totals[col.key] = rows
      .filter((row) => row.rowKind === "unit")
      .reduce((sum, row) => sum + (row.cells[col.key]?.total ?? 0), 0);
  });
  return totals;
};

/**
 * Additive overlay applied to a site-works `SectionMatrix` (Preliminaries,
 * Roads, Main Services, Substation, POS, Compound) AFTER `buildSiteWorksMatrices`
 * returns it — that section's own row/column generation is never modified.
 * When no item maps to this section, returns `matrix` unchanged (same array
 * reference).
 *
 * There is no plot/garage-style row-type split here — every row in these
 * sections already is a real road/element/area, so a mapped item's amount
 * splits equally across every `rowKind: "unit"` row, same "equal share across
 * every target row" convention `applyDynamicBillSummaryToPlotMatrixRows`
 * established for Plot Matrix. `billTotal` is bumped by the same amount as
 * `grandTotal` so the section's own Bill-Summary-reconciliation `variance`
 * stays zero — a manually mapped item is being deliberately folded into this
 * section's Run Matrix view, so it should read as "part of" the section's
 * total here, not as a discrepancy against a bill it was never on.
 */
export function applyRunMatrixMappingToSection(
  matrix: SectionMatrix,
  mappedItems: ResolvedRunMatrixMappedItem[],
): SectionMatrix {
  const itemsForSection = mappedItems.filter((m) => m.section === matrix.key);
  if (itemsForSection.length === 0) return matrix;

  const targets = matrix.rows.filter((row) => row.rowKind === "unit");
  if (targets.length === 0) return matrix;

  const rowsByKey = new Map<string, SectionMatrixRow>(
    matrix.rows.map((row) => [row.key, { ...row, cells: { ...row.cells } }]),
  );
  let billTotalIncrease = 0;

  itemsForSection.forEach(({ columnKey, amount }) => {
    if (!matrix.columns.some((c) => c.key === columnKey)) return;
    const share = amount / targets.length;
    billTotalIncrease += amount;
    targets.forEach((target) => {
      const row = rowsByKey.get(target.key);
      if (!row) return;
      const existingCell = row.cells[columnKey] ?? { total: 0, lines: [] };
      row.cells[columnKey] = {
        ...existingCell,
        total: existingCell.total + share,
        lines: [
          ...existingCell.lines,
          {
            description: "Dynamic Bill Summary mapping",
            qty: 1,
            unit: "",
            rate: share,
            amount: share,
            source: "Run Matrix Mapping",
          },
        ],
      };
      row.total += share;
    });
  });

  const rows = matrix.rows.map((row) => rowsByKey.get(row.key) ?? row);
  const columnTotals = recomputeColumnTotals(rows, matrix.columns);
  const grandTotal = rows
    .filter((row) => row.rowKind === "unit")
    .reduce((sum, row) => sum + row.total, 0);
  const billTotal = matrix.billTotal + billTotalIncrease;

  return { ...matrix, rows, columnTotals, grandTotal, billTotal, variance: billTotal - grandTotal };
}

/**
 * Additive overlay applied to the drainage `RunMatrixResult` AFTER
 * `buildRunMatrix` returns it. Drainage Runs already has a dedicated bucket
 * for exactly this category of item — the "Not run-specific" (`site-wide`)
 * row, one per discipline that has any rows at all — so a `drainage-foul`/
 * `drainage-storm` mapped item's amount splits equally across that
 * discipline's own site-wide row(s) only (never the other discipline's, and
 * never a `run` row), matching how manually-entered aggregate Bill Summary
 * items already land there (see `RunMatrix.tsx`'s "How allocation works"
 * modal). Returns `result` unchanged (same reference) when nothing maps to a
 * drainage section, or the mapped discipline has no site-wide row to receive
 * it (that discipline isn't configured on this tender yet).
 */
export function applyRunMatrixMappingToDrainage(
  result: RunMatrixResult,
  mappedItems: ResolvedRunMatrixMappedItem[],
): RunMatrixResult {
  const drainageItems = mappedItems.flatMap((m) => {
    const discipline = DRAINAGE_DISCIPLINE_BY_SECTION[m.section];
    return discipline ? [{ ...m, discipline }] : [];
  });
  if (drainageItems.length === 0) return result;

  const rowsByKey = new Map<string, RunMatrixRow>(
    result.rows.map((row) => [row.key, { ...row, cells: { ...row.cells } }]),
  );
  const disciplineIncrease: Record<string, number> = {};

  drainageItems.forEach(({ columnKey, amount, discipline }) => {
    if (!result.columns.some((c) => c.key === columnKey)) return;
    const targets = result.rows.filter((row) => row.rowKind === "site-wide" && row.discipline === discipline);
    if (targets.length === 0) return;
    const share = amount / targets.length;
    targets.forEach((target) => {
      const row = rowsByKey.get(target.key);
      if (!row) return;
      const existingCell = row.cells[columnKey] ?? { total: 0, lines: [] };
      row.cells[columnKey] = {
        ...existingCell,
        total: existingCell.total + share,
        lines: [
          ...existingCell.lines,
          {
            description: "Dynamic Bill Summary mapping",
            qty: 1,
            unit: "",
            rate: share,
            amount: share,
            source: "Run Matrix Mapping",
          },
        ],
      };
      row.total += share;
      disciplineIncrease[row.discipline] = (disciplineIncrease[row.discipline] ?? 0) + share;
    });
  });

  const rows = result.rows.map((row) => rowsByKey.get(row.key) ?? row);
  const columnTotals: Record<string, number> = {};
  result.columns.forEach((col) => {
    columnTotals[col.key] = rows
      .filter((row) => row.rowKind !== "separator")
      .reduce((sum, row) => sum + (row.cells[col.key]?.total ?? 0), 0);
  });
  const grandTotal = rows
    .filter((row) => row.rowKind !== "separator")
    .reduce((sum, row) => sum + row.total, 0);

  const disciplineTotals = { ...result.disciplineTotals };
  const reconciliation = { ...result.reconciliation };
  (Object.keys(disciplineIncrease) as (keyof typeof disciplineIncrease)[]).forEach((discipline) => {
    const increase = disciplineIncrease[discipline];
    disciplineTotals[discipline as "foul" | "storm"] =
      (disciplineTotals[discipline as "foul" | "storm"] ?? 0) + increase;
    const existing = reconciliation[discipline as "foul" | "storm"];
    if (existing) {
      const billTotal = existing.billTotal + increase;
      const siteWide = existing.siteWide + increase;
      reconciliation[discipline as "foul" | "storm"] = {
        ...existing,
        billTotal,
        siteWide,
        variance: billTotal - (existing.allocated + siteWide),
      };
    }
  });

  return { ...result, rows, columnTotals, grandTotal, disciplineTotals, reconciliation };
}
